import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import SettingsSystemDaydreamIcon from "@mui/icons-material/SettingsSystemDaydream";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import dayjs from "dayjs";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
const OUTCOME_PREVIEW_LINES = 5;

const ViewProgram = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { program_id } = location.state || {};

  const [overAllLoading, setOverAllLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Program Outcome "Read more": shown only when the text is taller than the collapsed preview
  const outcomeRef = useRef(null);
  const [showFullOutcome, setShowFullOutcome] = useState(false);
  const [isOutcomeLong, setIsOutcomeLong] = useState(false);

  useLayoutEffect(() => {
    const el = outcomeRef.current;
    // Measured while collapsed: clipped text means there is more to read
    if (el && !showFullOutcome) setIsOutcomeLong(el.scrollHeight > el.clientHeight + 1);
  }, [viewData, showFullOutcome]);

  useEffect(() => {
    if (program_id) {
      fetchProgramDetails(program_id);
    } else {
      navigate(-1);
    }
  }, [program_id]);

  const fetchProgramDetails = async (id) => {
    setOverAllLoading(true);
    try {
      const res = await api.post("/ViewProgramByID", { program_id: id });
      if (res?.data?.status === "success") {
        setViewData(res.data.data);
      } else {
        showSnackbar("error", res?.data?.message || "Failed to fetch program details");
      }
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Something went wrong");
    } finally {
      setOverAllLoading(false);
    }
  };

  if (!viewData && !overAllLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No program data found.
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading && <OverAllLoader message="Fetching program details..." />}
      {viewData && (
        <Box>
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "fit-content",
              mb: 2,
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          <Box>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Grid
                  sx={{
                    backgroundColor: "#1c97e3",
                    p: 0.5,
                    borderRadius: "10px",
                  }}
                >
                  <ListAltIcon sx={{ color: "white", fontSize: "35px" }} />
                </Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {getLabel('PROGRAMS')} Details
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}
            >
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> {getLabel('PROGRAMS')} Name</Typography>
                    <Typography>{viewData.program_name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Status</Typography>
                    <Typography>{viewData.status || "-"}</Typography>
                  </Grid>
                </Grid>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> {getLabel('PROGRAM_CODE')}</Typography>
                    {viewData.program_code ? (
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 1.2,
                          py: 0.3,
                          borderRadius: "8px",
                          fontWeight: 600,
                          fontSize: "12px",
                          backgroundColor: "#e4eefb",
                          color: "#1c97e3",
                          letterSpacing: "0.5px",
                          mt: 0.5,
                        }}
                      >
                        {viewData.program_code}
                      </Box>
                    ) : (
                      <Typography>-</Typography>
                    )}
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Display Name</Typography>
                    <Typography>{viewData.display_name || "-"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Program Outcome: long paragraph text gets its own section, shortened with Read more */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TrackChangesIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Program Outcome
                </Typography>
              </Grid>

              <Box>
                <Typography
                  ref={outcomeRef}
                  sx={{
                    // Keep line breaks / paragraphs as typed; wrap long unbroken words
                    whiteSpace: "pre-line",
                    overflowWrap: "anywhere",
                    lineHeight: 1.7,
                    // Collapsed: first OUTCOME_PREVIEW_LINES lines only (same line-clamp approach as QuestionPaperPreview)
                    ...(!showFullOutcome && {
                      display: "-webkit-box",
                      WebkitLineClamp: OUTCOME_PREVIEW_LINES,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }),
                  }}
                >
                  {viewData.program_outcome || "-"}
                </Typography>
                {isOutcomeLong && (
                  <Typography
                    onClick={() => setShowFullOutcome((prev) => !prev)}
                    sx={{
                      mt: 0.5,
                      width: "fit-content",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#1c97e3",
                    }}
                  >
                    {showFullOutcome ? "Read less" : "Read more"}
                  </Typography>
                )}
              </Box>

              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SettingsSystemDaydreamIcon
                  sx={{ color: "#1c97e3", fontSize: "30px" }}
                />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  System Information
                </Typography>
              </Grid>

              <Grid sx={{ display: "flex" }}>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Created By</Typography>
                  <Typography>{viewData.created_by || "-"}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Created Date</Typography>
                  <Typography>
                    {viewData.created_at
                      ? dayjs(viewData.created_at).format("DD-MM-YYYY")
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ViewProgram;
